import { ZONE_POSTAL_CODES, ZONES } from './state'
import { handlerSimulateInstallationPrice } from '@modules/installation-price-simulation/application/simulate-installation-price'
import { sanitizeAssets } from '@modules/installation-price-simulation/application/sanitize-assets'
import { getAssetsCatalog } from '@modules/assets-catalog/application/get-assets-catalog'

function postalCodeForZone(zone) {
  return ZONE_POSTAL_CODES[zone] ?? ZONE_POSTAL_CODES[ZONES.PENINSULA]
}

function normalizeTension(tension) {
  return typeof tension === 'string' ? tension.toUpperCase() : tension
}

function installationTypeFromTension(tension) {
  const normalized = normalizeTension(tension)
  if (typeof normalized !== 'string') return undefined
  if (normalized.startsWith('1X')) return 1
  if (normalized.startsWith('3X')) return 3
  return undefined
}

function buildSimulateBody(specs) {
  return {
    tension: normalizeTension(specs.tension),
    extraAdditionalCost: Number(specs.extraAdditionalCost) || 0,
    assets: sanitizeAssets(specs.assets),
    panels: Number(specs.panels),
    roofType: specs.roofType,
    panelsModel: specs.panelsModel,
    clientType: specs.clientType,
    energyReform: {
      hasEnergyReform: Boolean(specs.energyReform?.hasEnergyReform),
    },
    energyEfficiencyCertificateQuantity:
      Number(specs.energyEfficiencyCertificateQuantity) || 0,
    postalCode: postalCodeForZone(specs.zone),
    discounts: {
      installation: 0,
      panels: Number(specs.discounts?.panels) || 0,
      battery: Number(specs.discounts?.battery) || 0,
      evCharger: Number(specs.discounts?.evCharger) || 0,
      certificate: Number(specs.discounts?.certificate) || 0,
    },
  }
}

function updateOfferSpecs({ commit, state }, value) {
  commit('SET_SPECS', value)
  const hasFormChange = Object.keys(value || {}).some((k) => k !== 'discounts')
  if (hasFormChange && state.simulation) {
    commit('SET_SIMULATION', null)
  }
}

function updateDiscount({ commit }, discount) {
  commit('SET_DISCOUNT', discount)
}

function updateAssets({ commit, state }, assets) {
  const previousBatteryId = (state.specs.assets || []).find(
    (a) => a.type === 'battery' && a.amount > 0
  )?.id
  const previousEvChargerId = (state.specs.assets || []).find(
    (a) => a.type === 'evCharger' && a.amount > 0
  )?.id
  const nextBatteryId = (assets || []).find(
    (a) => a.type === 'battery' && a.amount > 0
  )?.id
  const nextEvChargerId = (assets || []).find(
    (a) => a.type === 'evCharger' && a.amount > 0
  )?.id

  const batteryModelChanged =
    !!previousBatteryId &&
    !!nextBatteryId &&
    previousBatteryId !== nextBatteryId
  const evChargerModelChanged =
    !!previousEvChargerId &&
    !!nextEvChargerId &&
    previousEvChargerId !== nextEvChargerId

  commit('SET_SPECS', { assets })

  if (batteryModelChanged || evChargerModelChanged) {
    const discounts = { ...state.specs.discounts }
    if (batteryModelChanged) discounts.battery = 0
    if (evChargerModelChanged) discounts.evCharger = 0
    commit('SET_DISCOUNT', discounts)
  }

  commit('RESET_ASSET_DISCOUNTS_IF_UNSELECTED')
  if (state.simulation) {
    commit('SET_SIMULATION', null)
  }
}

async function simulate({ state, commit }) {
  commit('SET_ERROR', null)
  commit('SET_IS_LOADING', true)

  const body = buildSimulateBody(state.specs)

  const onInstallationPriceSimulation = (simulation) => {
    commit('SET_SIMULATION', simulation)
    commit('SET_LAST_SIMULATED_PANELS', body.panels)
    commit('SET_APPLIED_DISCOUNTS', { ...state.specs.discounts })
    if (simulation?.installationTax !== undefined) {
      commit('SET_INSTALLATION_TAX', simulation.installationTax)
    }
    if (simulation?.taxType !== undefined) {
      commit('SET_TAX_TYPE', simulation.taxType)
    }
  }

  const onError = (error) => {
    commit('SET_ERROR', error)
  }

  await handlerSimulateInstallationPrice().execute(body, {
    onInstallationPriceSimulation,
    onError,
  })

  commit('SET_IS_LOADING', false)
}

async function applyDiscountAndRecalculate({ state, commit }) {
  commit('SET_APPLY_DISCOUNT_ERROR', null)
  commit('SET_IS_APPLYING_DISCOUNT', true)

  const body = buildSimulateBody(state.specs)

  const onInstallationPriceSimulation = (simulation) => {
    commit('SET_SIMULATION', simulation)
    commit('SET_LAST_SIMULATED_PANELS', body.panels)
    commit('SET_APPLIED_DISCOUNTS', { ...state.specs.discounts })
    if (simulation?.installationTax !== undefined) {
      commit('SET_INSTALLATION_TAX', simulation.installationTax)
    }
    if (simulation?.taxType !== undefined) {
      commit('SET_TAX_TYPE', simulation.taxType)
    }
  }

  const onError = (error) => {
    commit('SET_APPLY_DISCOUNT_ERROR', error)
  }

  await handlerSimulateInstallationPrice().execute(body, {
    onInstallationPriceSimulation,
    onError,
  })

  commit('SET_IS_APPLYING_DISCOUNT', false)
}

async function requestAssetsCatalog({ state, commit }) {
  const installationType = installationTypeFromTension(state.specs.tension)
  if (!installationType) return

  commit('SET_IS_LOADING_CATALOG', true)
  commit('SET_CATALOG_ERROR', false)

  const onAssetsCatalog = (assets) => {
    commit('SET_ASSETS_CATALOG', assets)
    commit('SET_IS_LOADING_CATALOG', false)
  }

  const onNetworkError = () => {
    commit('SET_ASSETS_CATALOG', [])
    commit('SET_IS_LOADING_CATALOG', false)
    commit('SET_CATALOG_ERROR', true)
  }

  try {
    await getAssetsCatalog().execute(
      {
        postalCode: postalCodeForZone(state.specs.zone),
        installationType,
      },
      { onAssetsCatalog, onNetworkError }
    )
  } catch (error) {
    onNetworkError(error)
  }
}

function resetState({ commit }) {
  commit('RESET_STATE')
}

export default {
  updateOfferSpecs,
  updateDiscount,
  updateAssets,
  simulate,
  applyDiscountAndRecalculate,
  requestAssetsCatalog,
  resetState,
}
